import { SaasRoleName } from "@intentsify/types";

/**
 * The intentsify.io restyle ("brand look") is opt-in for admins while it is
 * being rolled out; everyone else keeps the legacy look.
 *
 * The look is resolved once at startup and switching reloads the page, so
 * module-level values (the theme, `colors`, chart palettes) never have to
 * change at runtime.
 */
export const BRAND_LOOK_STORAGE_KEY = "intentsify-brand-look";

// Written by the zustand persist middleware in store/useAppStore.ts.
const APP_STORE_STORAGE_KEY = "root-app-local-storage";

const readStorage = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** The logged-in user (not the impersonated one) is an admin. */
const readIsAdmin = () => {
  const raw = readStorage(APP_STORE_STORAGE_KEY);

  if (!raw) {
    return false;
  }

  try {
    const persisted = JSON.parse(raw) as {
      state?: { user?: { saasRole?: string } | null };
    };

    return persisted.state?.user?.saasRole === SaasRoleName.Admin;
  } catch {
    return false;
  }
};

const resolveIsBrandLook = () =>
  typeof window !== "undefined" &&
  readStorage(BRAND_LOOK_STORAGE_KEY) === "on" &&
  readIsAdmin();

export const isBrandLook = resolveIsBrandLook();

export const setBrandLook = (enabled: boolean) => {
  if (enabled) {
    window.localStorage.setItem(BRAND_LOOK_STORAGE_KEY, "on");
  } else {
    window.localStorage.removeItem(BRAND_LOOK_STORAGE_KEY);
  }

  window.location.reload();
};

/** Picks the brand or legacy variant of a value for the active look. */
export const byLook = <T>(brand: T, legacy: T): T =>
  isBrandLook ? brand : legacy;
